import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthContext';
import { useApi } from '../lib/apiClient';
import { useQuery } from '@tanstack/react-query';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
    Skeleton,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Input,
    Tabs,
    TabsList,
    TabsTrigger,
} from "@pagespeedify/ui";
import { PlusCircle, TrendingUp, TrendingDown, Minus, Edit, Trash2, CheckCircle2Icon, XCircleIcon, AlertTriangleIcon, PauseCircle, Smartphone, Monitor, Command, AlertCircle } from 'lucide-react';
import { MonitoringUsageResponse, MonitoredUrl, MonitoringHistory, Insight, MetricStats, FlattenedMonitoringHistory } from '../types';
import { MonitoringTabs } from '../components/monitoring/MonitoringTabs';
import { getMetricsForPlan, getUserPlanType } from '../utils/monitoring';
import { CreateTaskForm } from '../components/tasks/CreateTaskForm';
import { EditMonitoringTaskForm } from '../components/monitoring/EditMonitoringTaskForm';
import { UserType } from '../lib/auth/types';
import { Task } from '../types';
import { TaskStatusBadge, TaskPriorityBadge } from '../components/tasks/TaskBadges';
import { toast } from 'sonner';
import { useDeleteQueue } from '../components/tasks/DeleteQueueContext';
import { QuickSettingsModal } from '../components/monitoring/QuickSettingsModal';
import { FieldDataFetcher } from '../components/monitoring/FieldDataFetcher';

// --- Helper to determine metric status ---
const getMetricStatus = (metric: string, value: number | null): 'good' | 'needs improvement' | 'poor' | 'neutral' => {
    if (value === null) return 'neutral';
    const thresholds = {
        performance_score: { good: 90, poor: 50 },
        lcp: { good: 2500, poor: 4000 },
        cls: { good: 0.1, poor: 0.25 },
        fcp: { good: 1800, poor: 3000 },
        ttfb: { good: 800, poor: 1800 },
        tbt: { good: 200, poor: 600 },
        tti: { good: 3800, poor: 7300 },
    };
    const metricKey = metric.toLowerCase() as keyof typeof thresholds;
    if (!thresholds[metricKey]) return 'neutral';

    const { good, poor } = thresholds[metricKey];

    if (metricKey === 'performance_score') {
        if (value >= good) return 'good';
        if (value < poor) return 'poor';
        return 'needs improvement';
    } else {
        if (value <= good) return 'good';
        if (value > poor) return 'poor';
        return 'needs improvement';
    }
};

const getRatingClasses = (rating: 'good' | 'needs improvement' | 'poor' | 'neutral' | undefined) => {
    switch (rating) {
        case 'good':
            return { text: 'text-report-good', bg: 'bg-report-good-light', fill: 'hsl(var(--report-good))', icon: CheckCircle2Icon };
        case 'needs improvement':
            return { text: 'text-report-improvement', bg: 'bg-report-improvement-light', fill: 'hsl(var(--report-improvement))', icon: AlertTriangleIcon };
        case 'poor':
            return { text: 'text-report-fail', bg: 'bg-report-fail-light', fill: 'hsl(var(--report-fail))', icon: XCircleIcon };
        default:
            return { text: 'text-muted-foreground', bg: 'bg-muted', fill: 'hsl(var(--muted-foreground))', icon: AlertTriangleIcon };
    }
};

const getPerformanceScoreClasses = (score: number) => {
    if (score >= 90) return getRatingClasses('good');
    if (score >= 50) return getRatingClasses('needs improvement');
    return getRatingClasses('poor');
};

// --- Component to display details for the selected URL ---
const MonitoringDetailView: React.FC<{
    selectedUrl: MonitoredUrl | null;
    isEnterprise: boolean;
    onEdit: (task: MonitoredUrl) => void;
    selectedTask: Task | null;
    monitoringUsage?: MonitoringUsageResponse;
    availableFormFactors: { mobile: boolean; desktop: boolean };
    selectedFormFactor: 'mobile' | 'desktop';
    setSelectedFormFactor: (formFactor: 'mobile' | 'desktop') => void;
}> = ({ selectedUrl, isEnterprise, onEdit, selectedTask, monitoringUsage, availableFormFactors, selectedFormFactor, setSelectedFormFactor }) => {
    const { user } = useAuth();
    const apiClient = useApi();
    const { addTaskToDeleteQueue } = useDeleteQueue();

    const { data: history, isLoading } = useQuery<FlattenedMonitoringHistory[], Error>({
        queryKey: ['monitoringHistory', selectedUrl?.id],
        queryFn: async () => {
            if (!apiClient || !selectedUrl) return [];
            const response = await apiClient.get<{ success: boolean; data: FlattenedMonitoringHistory[] }>(`/monitoring/history/${selectedUrl.id}`);
            if (!response.success || !response.data) {
                throw new Error('Failed to fetch monitoring history.');
            }
            return response.data;
        },
        enabled: !!user && !!apiClient && !!selectedUrl,
        staleTime: 60000, // Cache for 1 minute
        retry: 1,
        retryDelay: 5000,
        refetchOnWindowFocus: false,
    });

    // Fetch insights from monitoring detail endpoint
    const { data: insightsData } = useQuery<{ insights: Insight[]; stats: Record<string, MetricStats> }, Error>({
        queryKey: ['monitoringInsights', selectedUrl?.id],
        queryFn: async () => {
            if (!apiClient || !selectedUrl) return { insights: [], stats: {} };
            const response = await apiClient.get<{ success: boolean; data: any; insights: Insight[]; stats: Record<string, MetricStats> }>(`/monitoring/${selectedUrl.id}`);
            if (!response.success) {
                throw new Error('Failed to fetch insights.');
            }
            return { insights: response.insights || [], stats: response.stats || {} };
        },
        enabled: !!user && !!apiClient && !!selectedUrl,
        staleTime: 60000, // Cache for 1 minute
        retry: 1,
        retryDelay: 5000,
        refetchOnWindowFocus: false,
    });

    // Lab data date/time selection
    const [selectedLabDate, setSelectedLabDate] = useState<string | null>(null);
    const [selectedLabTimeScanId, setSelectedLabTimeScanId] = useState<string | null>(null);
    const [labDateSearchTerm, setLabDateSearchTerm] = useState('');

    // Field data date/time selection
    const [selectedFieldDate, setSelectedFieldDate] = useState<string | null>(null);
    const [selectedFieldTimeId, setSelectedFieldTimeId] = useState<string | null>(null);
    const [fieldDateSearchTerm, setFieldDateSearchTerm] = useState('');

    // Which tab is active
    const [scanDataType, setScanDataType] = useState<'lab' | 'field'>('lab');

    // ========== LAB DATA: Date/Time selection and derived values ==========
    const uniqueLabDates = useMemo(() => {
        if (!history) return [];
        const dates = history.map(h => new Date(h.scan_date).toLocaleDateString());
        return Array.from(new Set(dates)).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    }, [history]);

    const filteredLabDates = useMemo(() => {
        if (!labDateSearchTerm) return uniqueLabDates;
        return uniqueLabDates.filter(date => date.includes(labDateSearchTerm));
    }, [uniqueLabDates, labDateSearchTerm]);

    const scansForSelectedLabDate = useMemo(() => {
        if (!history || !selectedLabDate) return [];
        return history.filter(h => new Date(h.scan_date).toLocaleDateString() === selectedLabDate);
    }, [history, selectedLabDate]);

    useEffect(() => {
        if (uniqueLabDates.length > 0 && !selectedLabDate) {
            setSelectedLabDate(uniqueLabDates[0]);
        }
    }, [uniqueLabDates, selectedLabDate]);

    useEffect(() => {
        setSelectedLabTimeScanId(null);
    }, [selectedLabDate]);

    useEffect(() => {
        if (scansForSelectedLabDate.length > 0 && !selectedLabTimeScanId) {
            setSelectedLabTimeScanId(scansForSelectedLabDate[0].id);
        } else if (scansForSelectedLabDate.length === 0) {
            setSelectedLabTimeScanId(null);
        }
    }, [scansForSelectedLabDate, selectedLabTimeScanId]);

    const currentLabScan = useMemo(() => {
        if (!history || history.length === 0) return null;
        if (selectedLabTimeScanId) {
            return history.find(scan => scan.id === selectedLabTimeScanId) || history[0];
        }
        return history[0];
    }, [history, selectedLabTimeScanId]);

    const performanceMetricsToDisplay = useMemo(() => {
        if (!currentLabScan) return [];

        // Get allowed metrics for user's plan
        const userType = getUserPlanType(
            monitoringUsage?.accessContext?.planFeatures,
            user?.user_type
        );
        const allowedMetrics = getMetricsForPlan(userType);

        const metrics = [
            { key: 'lcp', title: 'Largest Contentful Paint (LCP)', value: currentLabScan.lcp, unit: 's', conversion: 1000, description: "Measures loading performance. To provide a good user experience, LCP should occur within 2.5 seconds of when the page first starts loading." },
            { key: 'cls', title: 'Cumulative Layout Shift (CLS)', value: currentLabScan.cls, unit: '', conversion: 1, description: "Measures visual stability. To provide a good user experience, pages should maintain a CLS of 0.1 or less." },
            { key: 'tbt', title: 'Total Blocking Time (TBT)', value: currentLabScan.tbt, unit: 's', conversion: 1000, description: "Measures the total amount of time that a page is blocked from responding to user input. A good TBT is 0.2 seconds or less." },
            { key: 'fcp', title: 'First Contentful Paint (FCP)', value: currentLabScan.fcp, unit: 's', conversion: 1000, description: "Marks the time at which the first text or image is painted. A good FCP is 1.8 seconds or less." },
            { key: 'tti', title: 'Time to Interactive (TTI)', value: currentLabScan.tti, unit: 's', conversion: 1000, description: "Measures the time from when the page starts loading to when its main sub-resources have loaded and it is capable of reliably responding to user input quickly. A good TTI is 3.8 seconds or less." },
            { key: 'ttfb', title: 'Time to First Byte (TTFB)', value: currentLabScan.ttfb, unit: 's', conversion: 1000, description: "Measures the time it takes for a user's browser to receive the first byte of page content. A good TTFB is 0.8 seconds or less." },
        ];

        return metrics
            .filter(m => m.value !== null && m.value !== undefined)
            .filter(m => allowedMetrics.includes(m.key))
            .map(m => ({ ...m, rating: getMetricStatus(m.key, m.value) }));
    }, [currentLabScan, monitoringUsage, user]);

    // ========== FIELD DATA: Date/Time selection and derived values ==========
    const fieldDataHistory = selectedUrl?.additional_metrics?.field_data_history || [];

    const uniqueFieldDates = useMemo(() => {
        if (fieldDataHistory.length === 0) return [];
        // Sort by fetched_at (ISO) desc first to ensure correct order regardless of locale format
        const sorted = [...fieldDataHistory].sort((a, b) => new Date(b.fetched_at).getTime() - new Date(a.fetched_at).getTime());
        const dates = sorted.map((h: any) => new Date(h.fetched_at).toLocaleDateString());
        return Array.from(new Set(dates));
    }, [fieldDataHistory]);

    const filteredFieldDates = useMemo(() => {
        if (!fieldDateSearchTerm) return uniqueFieldDates;
        return uniqueFieldDates.filter(date => date.includes(fieldDateSearchTerm));
    }, [uniqueFieldDates, fieldDateSearchTerm]);

    const scansForSelectedFieldDate = useMemo(() => {
        if (fieldDataHistory.length === 0 || !selectedFieldDate) return [];
        const filtered = fieldDataHistory.filter((h: any) => new Date(h.fetched_at).toLocaleDateString() === selectedFieldDate);
        return filtered;
    }, [fieldDataHistory, selectedFieldDate]);

    useEffect(() => {
        if (uniqueFieldDates.length > 0 && !selectedFieldDate) {
            setSelectedFieldDate(uniqueFieldDates[0]);
        }
    }, [uniqueFieldDates, selectedFieldDate]);

    // When selected date changes, auto-select the first time entry for that date
    useEffect(() => {
        if (scansForSelectedFieldDate.length > 0) {
            setSelectedFieldTimeId(scansForSelectedFieldDate[0]?.fetched_at);
        } else {
            setSelectedFieldTimeId(null);
        }
    }, [selectedFieldDate, scansForSelectedFieldDate]);

    const currentFieldData = useMemo(() => {
        if (fieldDataHistory.length === 0) return null;
        if (selectedFieldTimeId) {
            return fieldDataHistory.find((h: any) => h.fetched_at === selectedFieldTimeId) || fieldDataHistory[fieldDataHistory.length - 1];
        }
        return fieldDataHistory[fieldDataHistory.length - 1];
    }, [fieldDataHistory, selectedFieldTimeId]);

    // Field metrics to display - uses CrUX data + TBT from audits, shows INP instead of TTI
    // If CrUX is unavailable, falls back to PSI audit values
    const { fieldMetricsToDisplay, hasCruxData } = useMemo(() => {
        if (!currentFieldData) return { fieldMetricsToDisplay: [], hasCruxData: false };

        const crux = currentFieldData.crux || {};
        const audits = currentFieldData.audits || {};

        // Check if we have any CrUX data at all
        const cruxAvailable = Object.keys(crux).length > 0;

        // Helper to extract CrUX metric value (p75 percentile)
        const getCruxValue = (key: string) => {
            const metric = crux[key];
            return metric?.percentile ?? null;
        };

        // Helper to get PSI audit value as fallback
        const getAuditValue = (auditKey: string) => {
            const audit = audits[auditKey];
            return audit?.numericValue ?? null;
        };

        // Get allowed metrics for user's plan
        const userType = getUserPlanType(
            monitoringUsage?.accessContext?.planFeatures,
            user?.user_type
        );
        const allowedMetrics = getMetricsForPlan(userType);

        // Build metrics - use CrUX if available, else PSI audits
        const metrics = [
            {
                key: 'lcp',
                title: 'Largest Contentful Paint (LCP)',
                value: getCruxValue('LARGEST_CONTENTFUL_PAINT_MS') ?? getAuditValue('largest-contentful-paint'),
                unit: 's',
                conversion: 1000,
                description: cruxAvailable ? "28-day average from real users. LCP should be within 2.5 seconds." : "Simulated loading performance from PSI. LCP should be within 2.5 seconds.",
                source: getCruxValue('LARGEST_CONTENTFUL_PAINT_MS') !== null ? 'CrUX' : 'PSI'
            },
            {
                key: 'cls',
                title: 'Cumulative Layout Shift (CLS)',
                value: getCruxValue('CUMULATIVE_LAYOUT_SHIFT_SCORE') ?? getAuditValue('cumulative-layout-shift'),
                unit: '',
                conversion: 1,
                description: cruxAvailable ? "28-day average from real users. CLS should be 0.1 or less." : "Simulated visual stability from PSI. CLS should be 0.1 or less.",
                source: getCruxValue('CUMULATIVE_LAYOUT_SHIFT_SCORE') !== null ? 'CrUX' : 'PSI'
            },
            {
                key: 'tbt',
                title: 'Total Blocking Time (TBT)',
                value: getAuditValue('total-blocking-time'),
                unit: 's',
                conversion: 1000,
                description: "Time page is blocked from responding to input. Good TBT is 0.2s or less.",
                source: 'PSI'
            },
            {
                key: 'fcp',
                title: 'First Contentful Paint (FCP)',
                value: getCruxValue('FIRST_CONTENTFUL_PAINT_MS') ?? getAuditValue('first-contentful-paint'),
                unit: 's',
                conversion: 1000,
                description: cruxAvailable ? "28-day average from real users. FCP should be 1.8 seconds or less." : "Simulated first paint from PSI. FCP should be 1.8 seconds or less.",
                source: getCruxValue('FIRST_CONTENTFUL_PAINT_MS') !== null ? 'CrUX' : 'PSI'
            },
            {
                key: 'inp',
                title: 'Interaction to Next Paint (INP)',
                value: getCruxValue('INTERACTION_TO_NEXT_PAINT'),
                unit: 's',
                conversion: 1000,
                description: "28-day average from real users. Measures how quickly the page responds to interactions. Good INP is 200ms or less.",
                source: 'CrUX'
            },
            {
                key: 'ttfb',
                title: 'Time to First Byte (TTFB)',
                value: getCruxValue('EXPERIMENTAL_TIME_TO_FIRST_BYTE') ?? getAuditValue('server-response-time'),
                unit: 's',
                conversion: 1000,
                description: cruxAvailable ? "28-day average from real users. TTFB should be 0.8 seconds or less." : "Simulated server response from PSI. TTFB should be 0.8 seconds or less.",
                source: getCruxValue('EXPERIMENTAL_TIME_TO_FIRST_BYTE') !== null ? 'CrUX' : 'PSI'
            },
        ];

        // Map INP to the plan's 'tti' allowance since INP replaces TTI for field data
        const fieldAllowedMetrics = allowedMetrics.map(m => m === 'tti' ? 'inp' : m);

        const displayMetrics = metrics
            .filter(m => m.value !== null && m.value !== undefined)
            .filter(m => fieldAllowedMetrics.includes(m.key))
            .map(m => ({ ...m, rating: getMetricStatus(m.key === 'inp' ? 'tti' : m.key, m.value) }));

        return { fieldMetricsToDisplay: displayMetrics, hasCruxData: cruxAvailable };
    }, [currentFieldData, monitoringUsage, user]);

    if (!selectedUrl) {
        return (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <h3 className="text-lg font-semibold">No URL Selected</h3>
                <p className="text-muted-foreground mt-2">Select a URL from the dropdown to view its monitoring data.</p>
            </div>
        );
    }

    if (isLoading) {
        return <Skeleton className="h-48 w-full" />;
    }

    const handleDeleteTask = () => {
        if (selectedTask) {
            const success = addTaskToDeleteQueue(selectedTask);
            if (success) {
                toast.success(`Task "${selectedTask.title}" added to delete queue`);
            }
        }
    };



    const isPaused = selectedUrl.is_active === false;

    return (
        <Card className={`overflow-hidden ${isPaused ? 'opacity-60' : ''}`}>
            {isPaused && (
                <div className="bg-muted/80 border-b px-4 py-3 flex items-center gap-3 rounded-t-lg">
                    <PauseCircle className="h-5 w-5 text-muted-foreground" />
                    <div>
                        <span className="font-medium text-muted-foreground">Monitoring Paused</span>
                        <span className="text-sm text-muted-foreground ml-2">– Scans are disabled. Click Edit to resume.</span>
                    </div>
                </div>
            )}
            <CardHeader className="p-4 md:p-6 bg-muted/30">
                {/* Header Row: Device + URL + Actions */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="shrink-0 w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
                            {selectedUrl.additional_metrics?.form_factor === 'desktop' ? (
                                <Monitor className="h-4 w-4 text-muted-foreground" />
                            ) : (
                                <Smartphone className="h-4 w-4 text-muted-foreground" />
                            )}
                        </div>
                        <div className="flex-1 w-0 min-w-0">
                            <CardTitle className="text-sm md:text-base font-medium truncate block" title={selectedUrl.website_url}>
                                {selectedUrl.website_url}
                            </CardTitle>
                            {selectedTask && (
                                <div className="flex items-center gap-2 mt-1">
                                    <TaskStatusBadge status={selectedTask.status} />
                                    <TaskPriorityBadge priority={selectedTask.priority} />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => onEdit(selectedUrl)} className="h-8 w-8">
                            <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={handleDeleteTask} disabled={!selectedTask} className="h-8 w-8 text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 overflow-hidden">
                {selectedUrl.scan_in_progress ? (
                    <div className="text-center py-8 md:py-12 border-2 border-dashed rounded-lg px-4">
                        <h3 className="text-base md:text-lg font-semibold">Scan in progress...</h3>
                        <p className="text-muted-foreground mt-2 text-sm">Please wait a moment while we analyze your page.</p>
                    </div>
                ) : (!history || history.length === 0) ? (
                    selectedTask && !selectedTask.has_monitoring_history ? (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                            <h3 className="text-lg font-semibold">Your scan is about to start...</h3>
                            <p className="text-muted-foreground mt-2">The initial scan for this URL has not run yet. It should start automatically.</p>
                        </div>
                    ) : (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                            <h3 className="text-lg font-semibold">No scan data available</h3>
                            <p className="text-muted-foreground mt-2">We don't have any monitoring history for this URL yet.</p>
                        </div>
                    )
                ) : (
                    <>
                        {/* Individual Scan Viewer Section */}
                        <div className="rounded-lg bg-muted/30 p-4 mb-6">
                            <div className="flex flex-col gap-4">
                                {/* Header with Lab/Field Toggle */}
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-4">
                                        <div>
                                            <h3 className="text-sm font-semibold">Scan Results</h3>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {/* Mobile/Desktop Toggle */}
                                            {availableFormFactors.mobile && availableFormFactors.desktop && (
                                                <Tabs value={selectedFormFactor} onValueChange={(v) => setSelectedFormFactor(v as 'mobile' | 'desktop')}>
                                                    <TabsList>
                                                        <TabsTrigger value="mobile" className="flex items-center gap-1">
                                                            <Smartphone className="h-3 w-3" /> Mobile
                                                        </TabsTrigger>
                                                        <TabsTrigger value="desktop" className="flex items-center gap-1">
                                                            <Monitor className="h-3 w-3" /> Desktop
                                                        </TabsTrigger>
                                                    </TabsList>
                                                </Tabs>
                                            )}
                                            {/* Lab/Field Toggle */}
                                            <Tabs value={scanDataType} onValueChange={(v) => setScanDataType(v as 'lab' | 'field')}>
                                                <TabsList>
                                                    <TabsTrigger value="lab">Lab</TabsTrigger>
                                                    <TabsTrigger value="field">Field</TabsTrigger>
                                                </TabsList>
                                            </Tabs>
                                        </div>
                                    </div>
                                </div>

                                {/* === LAB DATA TAB === */}
                                {scanDataType === 'lab' && (
                                    <>
                                        {/* Lab Date/Time Selector */}
                                        {history && history.length > 1 && (
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs text-muted-foreground">Viewing:</span>
                                                <Select onValueChange={setSelectedLabDate} value={selectedLabDate || ''}>
                                                    <SelectTrigger className="w-[140px] h-8 text-xs">
                                                        <SelectValue placeholder="Select date" />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-[300px]">
                                                        <div className="p-2">
                                                            <Input
                                                                type="text"
                                                                placeholder="Search date..."
                                                                value={labDateSearchTerm}
                                                                onChange={(e) => setLabDateSearchTerm(e.target.value)}
                                                                onKeyDown={(e) => e.stopPropagation()}
                                                                className="w-full h-7 text-xs"
                                                            />
                                                        </div>
                                                        <div className="max-h-[200px] overflow-y-auto">
                                                            {filteredLabDates.map((dateString) => (
                                                                <SelectItem key={dateString} value={dateString}>
                                                                    {dateString}
                                                                </SelectItem>
                                                            ))}
                                                        </div>
                                                    </SelectContent>
                                                </Select>
                                                {scansForSelectedLabDate.length > 0 && (
                                                    <Select onValueChange={setSelectedLabTimeScanId} value={selectedLabTimeScanId || ''}>
                                                        <SelectTrigger className="w-[110px] h-8 text-xs">
                                                            <SelectValue>
                                                                {currentLabScan?.scan_date ? new Date(currentLabScan.scan_date).toLocaleTimeString() : "Time"}
                                                            </SelectValue>
                                                        </SelectTrigger>
                                                        <SelectContent className="max-h-[200px]">
                                                            {scansForSelectedLabDate.map((scan) => (
                                                                <SelectItem key={scan.id} value={scan.id}>
                                                                    {new Date(scan.scan_date).toLocaleTimeString()}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            </div>
                                        )}

                                        {/* Lab Score and Metrics */}
                                        {currentLabScan && (
                                            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
                                                <div className="flex flex-col items-center justify-center gap-4">
                                                    <div className="relative w-[120px] h-[120px]">
                                                        <svg className={`w-full h-full ${getPerformanceScoreClasses(currentLabScan.performance_score).text}`} viewBox="0 0 120 120">
                                                            <circle cx="60" cy="60" r="54" fill="none" stroke="hsl(var(--muted))" strokeWidth="12" />
                                                            <circle
                                                                cx="60" cy="60" r="54" fill="none" stroke="currentColor"
                                                                strokeWidth="12"
                                                                strokeDasharray="339.292"
                                                                strokeDashoffset={339.292 - (currentLabScan.performance_score / 100) * 339.292}
                                                                transform="rotate(-90 60 60)"
                                                                strokeLinecap="round"
                                                                style={{ transition: 'stroke-dashoffset 0.6s cubic-bezier(.4,2,.3,1)' }}
                                                            />
                                                        </svg>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <span className={`text-3xl font-bold ${getPerformanceScoreClasses(currentLabScan.performance_score).text}`}>
                                                                {Math.round(currentLabScan.performance_score)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        {new Date(currentLabScan.scan_date).toLocaleString()}
                                                    </p>
                                                </div>
                                                <div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {performanceMetricsToDisplay.map((metric) => {
                                                            const { text: color, icon: Icon } = getRatingClasses(metric.rating);
                                                            const displayValue = metric.unit === 's'
                                                                ? (Number(metric.value) / metric.conversion).toFixed(2)
                                                                : Number(metric.value).toFixed(metric.key === 'cls' ? 3 : 0);

                                                            return (
                                                                <TooltipProvider key={metric.title} delayDuration={1000}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="flex flex-col gap-1 p-3 rounded-lg bg-muted/40 cursor-pointer transition-colors hover:bg-muted/60">
                                                                                <div className="flex items-center gap-2 mb-1">
                                                                                    <Icon className={`w-5 h-5 ${color}`} />
                                                                                    <span className="text-sm font-medium text-foreground">{metric.title}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className={`text-2xl font-bold ${color}`}>
                                                                                        {displayValue} {metric.unit}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent side="bottom" className="max-w-xs text-sm">
                                                                            <p>{metric.description}</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* === FIELD DATA TAB === */}
                                {scanDataType === 'field' && (
                                    <>
                                        {fieldDataHistory.length > 0 ? (
                                            <>
                                                {/* Field Date/Time Selector - always show when we have data */}
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-xs text-muted-foreground">Viewing:</span>
                                                    {fieldDataHistory.length >= 1 ? (
                                                        <>
                                                            <Select onValueChange={setSelectedFieldDate} value={selectedFieldDate || ''}>
                                                                <SelectTrigger className="w-[140px] h-8 text-xs">
                                                                    <SelectValue>
                                                                        {selectedFieldDate || "Select date"}
                                                                    </SelectValue>
                                                                </SelectTrigger>
                                                                <SelectContent className="max-h-[300px]">
                                                                    <div className="p-2">
                                                                        <Input
                                                                            type="text"
                                                                            placeholder="Search date..."
                                                                            value={fieldDateSearchTerm}
                                                                            onChange={(e) => setFieldDateSearchTerm(e.target.value)}
                                                                            onKeyDown={(e) => e.stopPropagation()}
                                                                            className="w-full h-7 text-xs"
                                                                        />
                                                                    </div>
                                                                    <div className="max-h-[200px] overflow-y-auto">
                                                                        {filteredFieldDates.map((dateString) => (
                                                                            <SelectItem key={dateString} value={dateString}>
                                                                                {dateString}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </div>
                                                                </SelectContent>
                                                            </Select>
                                                            {scansForSelectedFieldDate.length > 0 && (
                                                                <Select onValueChange={setSelectedFieldTimeId} value={selectedFieldTimeId || ''}>
                                                                    <SelectTrigger className="w-[110px] h-8 text-xs">
                                                                        <SelectValue placeholder="Time" />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="max-h-[200px]">
                                                                        {scansForSelectedFieldDate.map((scan: any) => (
                                                                            <SelectItem key={scan.report_id || scan.fetched_at} value={scan.fetched_at}>
                                                                                {new Date(scan.fetched_at).toLocaleTimeString()}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">
                                                            {currentFieldData ? new Date(currentFieldData.fetched_at).toLocaleString() : 'Latest'}
                                                        </span>
                                                    )}
                                                    {/* Compact Refresh Button in header */}
                                                    <FieldDataFetcher
                                                        monitoringId={selectedUrl?.id || ''}
                                                        websiteUrl={selectedUrl?.website_url || ''}
                                                        strategy={selectedUrl?.additional_metrics?.form_factor === 'desktop' ? 'desktop' : 'mobile'}
                                                        hasFieldData={true}
                                                        initialFieldDataCount={fieldDataHistory.length}
                                                        compact
                                                    />
                                                </div>

                                                {/* No CrUX Data Warning */}
                                                {!hasCruxData && (
                                                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                                        <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                                                        <div>
                                                            <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                                                                CrUX data not available
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                                Real user data (CrUX) is not available for this URL. This is common for new or low-traffic sites.
                                                                Showing simulated performance data from PageSpeed Insights instead.
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Field Score and Metrics - SAME LAYOUT AS LAB */}
                                                {currentFieldData && (
                                                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
                                                        <div className="flex flex-col items-center justify-center gap-4">
                                                            <div className="relative w-[120px] h-[120px]">
                                                                <svg className={`w-full h-full ${getPerformanceScoreClasses(currentFieldData.performance?.score || 0).text}`} viewBox="0 0 120 120">
                                                                    <circle cx="60" cy="60" r="54" fill="none" stroke="hsl(var(--muted))" strokeWidth="12" />
                                                                    <circle
                                                                        cx="60" cy="60" r="54" fill="none" stroke="currentColor"
                                                                        strokeWidth="12"
                                                                        strokeDasharray="339.292"
                                                                        strokeDashoffset={339.292 - ((currentFieldData.performance?.score || 0) / 100) * 339.292}
                                                                        transform="rotate(-90 60 60)"
                                                                        strokeLinecap="round"
                                                                        style={{ transition: 'stroke-dashoffset 0.6s cubic-bezier(.4,2,.3,1)' }}
                                                                    />
                                                                </svg>
                                                                <div className="absolute inset-0 flex items-center justify-center">
                                                                    <span className={`text-3xl font-bold ${getPerformanceScoreClasses(currentFieldData.performance?.score || 0).text}`}>
                                                                        {Math.round(currentFieldData.performance?.score || 0)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground">
                                                                {new Date(currentFieldData.fetched_at).toLocaleString()}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                {fieldMetricsToDisplay.length > 0 ? (
                                                                    fieldMetricsToDisplay.map((metric: any) => {
                                                                        const { text: color, icon: Icon } = getRatingClasses(metric.rating);
                                                                        const displayValue = metric.unit === 's'
                                                                            ? (Number(metric.value) / metric.conversion).toFixed(2)
                                                                            : Number(metric.value).toFixed(metric.key === 'cls' ? 3 : 0);

                                                                        return (
                                                                            <TooltipProvider key={metric.title} delayDuration={1000}>
                                                                                <Tooltip>
                                                                                    <TooltipTrigger asChild>
                                                                                        <div className="flex flex-col gap-1 p-3 rounded-lg bg-muted/40 cursor-pointer transition-colors hover:bg-muted/60">
                                                                                            <div className="flex items-center gap-2 mb-1">
                                                                                                <Icon className={`w-5 h-5 ${color}`} />
                                                                                                <span className="text-sm font-medium text-foreground">{metric.title}</span>
                                                                                                {metric.source && (
                                                                                                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                                                                                        {metric.source}
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <span className={`text-2xl font-bold ${color}`}>
                                                                                                    {displayValue} {metric.unit}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </TooltipTrigger>
                                                                                    <TooltipContent side="bottom" className="max-w-xs text-sm">
                                                                                        <p>{metric.description}</p>
                                                                                        <p className="text-xs text-muted-foreground mt-1">Source: {metric.source === 'CrUX' ? 'Real user data' : 'PageSpeed Insights'}</p>
                                                                                    </TooltipContent>
                                                                                </Tooltip>
                                                                            </TooltipProvider>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <p className="text-sm text-muted-foreground col-span-2">
                                                                        No metrics available. Try fetching the latest field data.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <FieldDataFetcher
                                                monitoringId={selectedUrl?.id || ''}
                                                websiteUrl={selectedUrl?.website_url || ''}
                                                strategy={selectedUrl?.additional_metrics?.form_factor === 'desktop' ? 'desktop' : 'mobile'}
                                                hasFieldData={false}
                                                initialFieldDataCount={0}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Divider and Insights Tabs Section */}
                        <div className="mt-6">
                            <MonitoringTabs
                                history={history || []}
                                fieldDataHistory={selectedUrl?.additional_metrics?.field_data_history || []}
                                selectedUrl={selectedUrl}
                                insights={insightsData?.insights || []}
                                stats={insightsData?.stats || {}}
                                monitoringUsage={monitoringUsage}
                                user={user}
                            />
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default function MonitoringPage() {
    const { user, isLoading: authIsLoading } = useAuth();
    const apiClient = useApi();
    const location = useLocation();
    const [selectedWebsiteUrl, setSelectedWebsiteUrl] = useState<string | null>(null);
    const [selectedFormFactor, setSelectedFormFactor] = useState<'mobile' | 'desktop'>('mobile');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingUrl, setEditingUrl] = useState<MonitoredUrl | null>(null);
    const [editingTask, setEditingTask] = useState<Task | null>(null);
    const [isQuickSettingsOpen, setIsQuickSettingsOpen] = useState(false);

    const isTeamOwner = user ? (!user.team_id || user.internalUserId === user.team_owner_id) : false;

    const { data: monitoringUsage, isLoading: isUsageLoading } = useQuery<MonitoringUsageResponse, Error>({
        queryKey: ['monitoringUsage', user?.uid],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            const response = await apiClient.get<{ success: boolean; usage: MonitoringUsageResponse }>('/monitoring/usage');
            if (!response.success || !response.usage) throw new Error('Failed to fetch monitoring usage data.');
            return response.usage;
        },
        enabled: !!user && !authIsLoading && !!apiClient,
        staleTime: 60000, // Cache for 1 minute
        retry: 1,
        retryDelay: 5000,
        refetchOnWindowFocus: false,
    });

    const { data: monitoredUrlsData, isLoading: isMonitorsLoading, refetch: refetchMonitors } = useQuery<MonitoredUrl[], Error>({
        queryKey: ['monitoredUrls', user?.uid],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            const response = await apiClient.get<{ success: boolean; data: MonitoredUrl[] }>('/monitoring');
            if (!response.success || !response.data) throw new Error('Failed to fetch monitored URLs.');
            return response.data;
        },
        enabled: !!user && !authIsLoading && !!apiClient,
        staleTime: 60000, // Cache for 1 minute
        retry: 1, // Only retry once on failure
        retryDelay: 5000, // Wait 5 seconds before retry
        refetchOnWindowFocus: false, // Don't refetch when user tabs back
    });

    const { addTaskToDeleteQueue, queuedDeleteTasks } = useDeleteQueue();
    const monitoredUrls = useMemo(() => {
        const urls = monitoredUrlsData || [];
        return urls.filter(url => {
            // Check if URL itself is deleted (not typical for task queue but safe)
            if (queuedDeleteTasks.some(qt => qt.id === url.id && qt.status !== 'failed')) return false;
            // Check if the associated task is deleted (more common linkage)
            if (url.latest_task_id && queuedDeleteTasks.some(qt => qt.id === url.latest_task_id && qt.status !== 'failed')) return false;
            return true;
        });
    }, [monitoredUrlsData, queuedDeleteTasks]);

    const { data: tasksResponse, isLoading: isTasksLoading } = useQuery<any, Error>({
        queryKey: ['ai-tasks-list'],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            return apiClient.get('/tasks?type=monitoring');
        },
        enabled: !!user && !authIsLoading && !!apiClient,
        staleTime: 60000, // Cache for 1 minute
        retry: 1,
        retryDelay: 5000,
        refetchOnWindowFocus: false,
    });
    const tasks = tasksResponse?.data?.tasks?.tasks || [];

    useEffect(() => {
        const state = location.state as { editingTask?: Task; selectedTask?: Task };

        // Helper to check if task is queued for delete
        const isQueuedForDelete = (id: string) => queuedDeleteTasks.some(t => t.id === id && t.status !== 'failed');

        if (state?.editingTask?.type === 'monitoring') {
            if (!isQueuedForDelete(state.editingTask.id)) {
                const url = monitoredUrls?.find(u => u.id === state.editingTask?.source_id);
                if (url) {
                    setEditingUrl(url);
                    setEditingTask(state.editingTask);
                }
            }
        } else if (state?.selectedTask && state.selectedTask.type === 'monitoring') {
            if (!isQueuedForDelete(state.selectedTask.id)) {
                if (state.selectedTask.source_id) {
                    const url = monitoredUrls?.find(u => u.id === state.selectedTask?.source_id);
                    if (url) {
                        setSelectedWebsiteUrl(url.website_url);
                        const formFactor = url.additional_metrics?.form_factor || 'mobile';
                        setSelectedFormFactor(formFactor as 'mobile' | 'desktop');
                    }
                }
            }
        }
    }, [location.state, monitoredUrls, queuedDeleteTasks]);

    // Handle URL query parameters for taskId (Deep Linking)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const taskIdFromUrl = searchParams.get('taskId');

        // Helper to check if task is queued for delete
        const isQueuedForDelete = (id: string) => queuedDeleteTasks.some(t => t.id === id && t.status !== 'failed');

        if (taskIdFromUrl && tasks && monitoredUrls) {
            if (!isQueuedForDelete(taskIdFromUrl)) {
                const targetTask = tasks.find((t: Task) => t.id === taskIdFromUrl);
                if (targetTask && targetTask.source_id) {
                    const url = monitoredUrls.find(u => u.id === targetTask.source_id);
                    if (url) {
                        setSelectedWebsiteUrl(url.website_url);
                        const formFactor = url.additional_metrics?.form_factor || 'mobile';
                        setSelectedFormFactor(formFactor as 'mobile' | 'desktop');
                    }
                }
            }
        }
    }, [location.search, tasks, monitoredUrls, queuedDeleteTasks]);

    // Group URLs by website_url to handle mobile/desktop as tabs
    const groupedUrls = useMemo(() => {
        if (!monitoredUrls) return new Map<string, { mobile?: MonitoredUrl; desktop?: MonitoredUrl }>();
        const grouped = new Map<string, { mobile?: MonitoredUrl; desktop?: MonitoredUrl }>();
        monitoredUrls.forEach(url => {
            const existing = grouped.get(url.website_url) || {};
            const formFactor = url.additional_metrics?.form_factor || 'mobile';
            if (formFactor === 'desktop') {
                existing.desktop = url;
            } else {
                existing.mobile = url;
            }
            grouped.set(url.website_url, existing);
        });
        return grouped;
    }, [monitoredUrls]);

    const uniqueWebsiteUrls = useMemo(() => Array.from(groupedUrls.keys()), [groupedUrls]);

    useEffect(() => {
        if (!selectedWebsiteUrl && uniqueWebsiteUrls.length > 0) {
            setSelectedWebsiteUrl(uniqueWebsiteUrls[0]);
        }
    }, [uniqueWebsiteUrls, selectedWebsiteUrl]);

    // Get the available form factors for the selected URL
    const availableFormFactors = useMemo(() => {
        if (!selectedWebsiteUrl) return { mobile: false, desktop: false };
        const group = groupedUrls.get(selectedWebsiteUrl);
        return {
            mobile: !!group?.mobile,
            desktop: !!group?.desktop
        };
    }, [groupedUrls, selectedWebsiteUrl]);

    // Auto-select valid form factor when URL changes
    useEffect(() => {
        if (selectedWebsiteUrl) {
            const group = groupedUrls.get(selectedWebsiteUrl);
            if (selectedFormFactor === 'mobile' && !group?.mobile && group?.desktop) {
                setSelectedFormFactor('desktop');
            } else if (selectedFormFactor === 'desktop' && !group?.desktop && group?.mobile) {
                setSelectedFormFactor('mobile');
            }
        }
    }, [selectedWebsiteUrl, groupedUrls, selectedFormFactor]);

    const selectedUrl = useMemo(() => {
        if (!selectedWebsiteUrl) return null;
        const group = groupedUrls.get(selectedWebsiteUrl);
        return (selectedFormFactor === 'desktop' ? group?.desktop : group?.mobile) || group?.mobile || group?.desktop || null;
    }, [groupedUrls, selectedWebsiteUrl, selectedFormFactor]);

    const selectedTask = useMemo(() => {
        return tasks?.find(t => t.source_id === selectedUrl?.id) || null;
    }, [tasks, selectedUrl]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | undefined;
        if (selectedUrl && (selectedUrl.scan_in_progress || (selectedTask && !selectedTask.has_monitoring_history))) {
            intervalId = setInterval(() => {
                refetchMonitors();
            }, 5000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [selectedUrl, selectedTask, refetchMonitors]);

    const canCreate = monitoringUsage?.canCreate.monitoring ?? false;
    const isEnterprise = monitoringUsage?.accessContext?.planFeatures?.plan_user_type_granted === 'enterprise';

    // Keyboard shortcut for Quick Settings (Ctrl+K / Cmd+K)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsQuickSettingsOpen(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleFormClose = () => {
        setShowCreateForm(false);
        refetchMonitors();
    };


    const addUrlButton = (
        <Button disabled={!canCreate || isUsageLoading} onClick={() => setShowCreateForm(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add URL
        </Button>
    );

    const handleEditMonitoringTask = (url: MonitoredUrl) => {
        const task = tasks?.find(t => t.source_id === url.id);
        if (task) {
            setEditingUrl(url);
            setEditingTask(task);
        } else {
            toast.error('Could not find the corresponding task to edit.');
        }
    };

    return (
        <>
            <div className="container mx-auto p-4 md:p-6 space-y-4 md:space-y-6 max-w-full overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold">Monitoring</h1>
                        <p className="text-muted-foreground text-sm md:text-base">Continuously track your site's performance and get alerted when metrics drop.</p>
                    </div>
                    {!showCreateForm && !editingTask && isTeamOwner && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span tabIndex={0}>{addUrlButton}</span>
                                </TooltipTrigger>
                                {!canCreate && !isUsageLoading && (
                                    <TooltipContent>
                                        <p>{monitoringUsage?.canCreate.monitoringReason || "You have reached your plan's limit for monitored URLs."}</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>

                {editingUrl && editingTask ? (
                    <EditMonitoringTaskForm monitoredUrl={editingUrl} task={editingTask} onClose={() => { setEditingUrl(null); setEditingTask(null); }} />
                ) : showCreateForm ? (
                    <CreateTaskForm initialType="monitoring" onClose={handleFormClose} />
                ) : (
                    <>
                        {isMonitorsLoading || isTasksLoading ? (
                            <Skeleton className="h-10 w-full max-w-sm mb-6" />
                        ) : monitoredUrls && monitoredUrls.length > 0 ? (
                            <div className="flex items-center gap-4 mb-6 flex-wrap">
                                {/* Quick Settings Trigger */}
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => setIsQuickSettingsOpen(true)}
                                    className="gap-1.5 text-sm bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:!text-black dark:hover:bg-gray-100"
                                >
                                    <Command className="h-3.5 w-3.5" />
                                    Quick Settings
                                    <kbd className="pointer-events-none hidden h-4 select-none items-center gap-1 rounded border bg-white/20 px-1 font-mono text-[9px] font-medium opacity-100 sm:flex dark:bg-black/10 dark:!text-black">⌘K</kbd>
                                </Button>
                            </div>
                        ) : (
                            <div className="text-center py-12 border-2 border-dashed rounded-lg">
                                <h3 className="text-lg font-semibold">No URLs are being monitored</h3>
                                {isTeamOwner ? (
                                    <p className="text-muted-foreground mt-2">Click "Add URL" to start monitoring your first website.</p>
                                ) : (
                                    <p className="text-muted-foreground mt-2">Your team has no URLs being monitored.</p>
                                )}
                            </div>
                        )}

                        {monitoredUrls && monitoredUrls.length > 0 && (
                            <MonitoringDetailView
                                selectedUrl={selectedUrl}
                                isEnterprise={isEnterprise}
                                onEdit={handleEditMonitoringTask}
                                selectedTask={selectedTask}

                                monitoringUsage={monitoringUsage}
                                availableFormFactors={availableFormFactors}
                                selectedFormFactor={selectedFormFactor}
                                setSelectedFormFactor={setSelectedFormFactor}
                            />
                        )}
                    </>
                )}
            </div >

            {/* Quick Settings Modal */}
            {
                monitoredUrls && (
                    <QuickSettingsModal
                        isOpen={isQuickSettingsOpen}
                        onClose={() => setIsQuickSettingsOpen(false)}
                        groupedUrls={groupedUrls}
                        monitoredUrls={monitoredUrls}
                        selectedWebsiteUrl={selectedWebsiteUrl}
                        onSelectUrl={(url) => setSelectedWebsiteUrl(url)}
                        onSuccess={() => refetchMonitors()}
                    />
                )
            }
        </>
    );
}
